import { notifications } from '@mantine/notifications'
import type { AppRouter } from '@markku/backend/trpc/router'
import type { QueryOptions } from '@markku/types'
import type { CreateTRPCReact } from '@trpc/react-query'

/**
 * Creates property-related hooks with the provided tRPC client.
 * Full type safety is preserved - all return types are inferred from the AppRouter.
 *
 * @param trpc - The typed tRPC client from the consuming app
 * @returns Object containing all property-related hooks with full type inference
 */
export function createPropertyHooks(trpc: CreateTRPCReact<AppRouter, unknown>) {
  return {
    useList: () =>
      trpc.property.list.useQuery(
        {},
        {
          staleTime: 60 * 1000, // 1 minute - ensures proposals appear quickly when user returns to app
        },
      ),

    useGetById: (id: number, options?: QueryOptions) =>
      trpc.property.getById.useQuery({ id }, options),

    useCreate: () => {
      const utils = trpc.useUtils()

      return trpc.property.create.useMutation({
        onSuccess: async (createdProperty) => {
          // Cache the newly created property
          utils.property.getById.setData(
            { id: createdProperty.id },
            createdProperty,
          )

          // Invalidate list to show the new property
          await utils.property.list.invalidate()

          // Force refetch of customer queries to immediately show new/updated data
          // This is especially important when auto-creating customer for first property
          await utils.customer.list.invalidate(undefined, {
            refetchType: 'active',
          })
          await utils.customer.getById.invalidate()

          // Invalidate user.me to refresh customerMemberships
          // When creating first property, backend creates customerMember with role='owner'
          // Without this, usePermissions().isOwnerOf() returns false until page reload
          await utils.user.me.invalidate()

          notifications.show({
            title: 'Luotu',
            message: 'Kiinteistö luotu onnistuneesti',
            color: 'green',
          })
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: async (error: any) => {
          notifications.show({
            title: 'Virhe',
            message: error?.message || 'Kiinteistön luonti epäonnistui',
            color: 'red',
          })
        },
      })
    },

    useUpdate: () => {
      const utils = trpc.useUtils()

      return trpc.property.update.useMutation({
        onSuccess: async (updatedProperty) => {
          // Direct cache update with server response - no refetch needed!
          utils.property.getById.setData(
            { id: updatedProperty.id },
            updatedProperty,
          )

          // Invalidate list in case it's displayed elsewhere
          utils.property.list.invalidate()

          notifications.show({
            title: 'Tallennettu',
            message: 'Kiinteistön tiedot päivitetty',
            color: 'green',
          })
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: async (error: any) => {
          notifications.show({
            title: 'Virhe',
            message: error?.message || 'Tietojen päivitys epäonnistui',
            color: 'red',
          })
        },
      })
    },
  }
}

export type PropertyHooks = ReturnType<typeof createPropertyHooks>
